import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { roundToDecimals } from '../../../utils/numberFormat'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function ConcreteBendingMomentByRebar() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [bw, setBw] = useState('')
  const [d, setD] = useState('')
  const [as, setAs] = useState('')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)
  const [stressUnit, setStressUnit] = useState('MPa')
  const [lengthUnit, setLengthUnit] = useState('cm')

  const HISTORY_KEY = 'concrete-bending-moment-by-rebar-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setBw(entry.bw)
      setD(entry.d)
      setAs(entry.as)
      setResult(entry.result)
      setData(entry.data)
      setStressUnit(entry.stressUnit || 'MPa')
      setLengthUnit(entry.lengthUnit || 'cm')
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.bw = validateField('bw', bw)
    newErrors.d = validateField('d', d)
    newErrors.as = validateField('as', as)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [bw, d, fck, fyk, as, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const convertToStandardUnits = () => {
    let convertedFck = fck
    let convertedFyk = fyk
    let convertedBw = bw
    let convertedD = d

    if (stressUnit === 'kN/cm²') {
      convertedFck = fck * 10
      convertedFyk = fyk * 10
    }

    if (lengthUnit === 'm') {
      convertedBw = bw * 100
      convertedD = d * 100
    }

    return {
      fck: convertedFck,
      fyk: convertedFyk,
      bw: convertedBw,
      d: convertedD,
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const convertedValues = convertToStandardUnits()
      const data = {
        ...convertedValues,
        as,
      }

      setData(data)

      const response = await api.post('/concrete/bending-moment-by-rebar', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        bw,
        d,
        as,
        result: response.data,
        data,
        stressUnit,
        lengthUnit,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      bw,
      d,
      fck,
      fyk,
      as,
      result,
      data,
      stressUnit,
      lengthUnit,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Flexão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setBw(jsonData.bw)
        setD(jsonData.d)
        setAs(jsonData.as)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setResult(jsonData.result)
        setData(jsonData.data)
        setStressUnit(jsonData.stressUnit || 'MPa')
        setLengthUnit(jsonData.lengthUnit || 'cm')
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Momento por Área</span>
          </div>
          <h1>📏 Momento por Área de Aço</h1>
          <p className="subtitle">
            Cálculo do momento resistente a partir da área de aço existente em seções retangulares
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Domínios 2 e 3</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta calcula o momento fletor resistente de seções retangulares de concreto armado a partir da
              área de aço existente, considerando os domínios de deformação 2 e 3 conforme a NBR 6118:2014.
            </p>
            <p>
              Ideal para verificação de seções existentes ou para determinar a capacidade resistente de elementos já
              armados.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Verificação de Seções</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto ({stressUnit})</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço ({stressUnit})</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura ({lengthUnit})</span>
              <ValidatedInput
                name="bw"
                type="number"
                value={bw}
                onChange={(event) => setBw(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil ({lengthUnit})</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Área de aço (cm²)</span>
              <ValidatedInput
                name="as"
                type="number"
                value={as ?? ''}
                onChange={(event) => setAs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Posição da linha neutra</h3>
              <MathDiv>
                <BlockMath math={`x= ${roundToDecimals(result.x)} \\text{ cm}`} />
              </MathDiv>
              <h3>Domínio de deformação</h3>
              {result.domain == 2 && (
                <>
                  <MathDiv>
                    <BlockMath math={`\\text{Seção no domínio 2.}`} />
                  </MathDiv>
                  <h3>Cálculo do momento</h3>
                  <MathDiv>
                    <BlockMath math={`M_d  = ${roundToDecimals(result.md)} \\text{ kN.cm}`} />
                  </MathDiv>
                </>
              )}
              {result.domain == 3 && (
                <>
                  <MathDiv>
                    <BlockMath math={`\\text{Seção no domínio 3.}`} />
                  </MathDiv>
                  <h3>Cálculo do momento</h3>
                  <MathDiv>
                    <BlockMath math={`M_d  = ${roundToDecimals(result.md)} \\text{ kN.cm}`} />
                  </MathDiv>
                </>
              )}
              {result.domain == 4 && (
                <>
                  <MathDiv>
                    <BlockMath math={`\\text{Seção no domínio 4. Essa condição não foi implementada.}`} />
                  </MathDiv>
                </>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Posição da linha neutra</h3>
              <MathDiv>
                <BlockMath
                  math={`x= \\dfrac{A_s \\cdot f_{yd}}{0.68 \\cdot b_w \\cdot f_{cd}} = \\dfrac{${data.as} \\cdot ${roundToDecimals(result.fyd / 10)} }{0.68 \\cdot ${data.bw} \\cdot ${roundToDecimals(result.fcd / 10)} }`}
                />
                <BlockMath math={`\\boxed{x= ${roundToDecimals(result.x)} \\text{ cm}}`} />
              </MathDiv>
              <h3>Domínio de deformação</h3>
              <MathDiv>
                <BlockMath
                  math={`x_{23}= \\dfrac{\\epsilon_c}{\\epsilon_c + \\epsilon_s} \\cdot d = \\dfrac{ 3.5 ‰ }{3.5 ‰ + 10 ‰} \\cdot ${data.d} = ${roundToDecimals(result.x23)} \\text{ cm}`}
                />
                <BlockMath
                  math={`x_{34}= \\dfrac{\\epsilon_c}{\\epsilon_c + \\epsilon_{yd}} \\cdot d = \\dfrac{ 3.5 ‰ }{3.5 ‰ + ${roundToDecimals(result.eyd * 1000)} ‰} \\cdot ${data.d} = ${roundToDecimals(result.x34)} \\text{ cm}`}
                />
              </MathDiv>
              {result.domain == 2 && (
                <>
                  <MathDiv>
                    <BlockMath math={`x < x_{23}`} />
                    <BlockMath math={`\\boxed{\\text{Seção no domínio 2.}}`} />
                  </MathDiv>
                  <h3>Cálculo do momento</h3>
                  <MathDiv>
                    <BlockMath
                      math={`M_d =  A_s \\cdot f_{yd} \\cdot (d - 0.4 \\cdot x)=  ${data.as} \\cdot ${roundToDecimals(result.fyd / 10)} \\cdot (${data.d} - 0.4 \\cdot ${roundToDecimals(result.x)}) = ${roundToDecimals(result.md)} \\text{ kN.cm}`}
                    />
                    <BlockMath math={`\\boxed{M_d  = ${roundToDecimals(result.md)} \\text{ kN.cm}}`} />
                  </MathDiv>
                </>
              )}
              {result.domain == 3 && (
                <>
                  <MathDiv>
                    <BlockMath math={`x_{23} < x < x_{34}`} />
                    <BlockMath math={`\\boxed{\\text{Seção no domínio 3.}}`} />
                  </MathDiv>
                  <h3>Cálculo do momento</h3>
                  <MathDiv>
                    <BlockMath
                      math={`M_d =  A_s \\cdot f_{yd} \\cdot (d - 0.4 \\cdot x)=  ${data.as} \\cdot ${roundToDecimals(result.fyd / 10)} \\cdot (${data.d} - 0.4 \\cdot ${roundToDecimals(result.x)}) = ${roundToDecimals(result.md)} \\text{ kN.cm}`}
                    />
                    <BlockMath math={`\\boxed{M_d  = ${roundToDecimals(result.md)} \\text{ kN.cm}}`} />
                  </MathDiv>
                </>
              )}
              {result.domain == 4 && (
                <>
                  <MathDiv>
                    <BlockMath math={`x_{34} < x`} />
                    <BlockMath math={`\\boxed{\\text{Seção no domínio 4. Essa condição não foi implementada.}}`} />
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
